import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Per-route SEO. Pages that already set their own title (courier pages, About)
// are intentionally omitted so they self-manage; this still sets canonical for them.
const BRAND = 'XFas Logistics';
const ORIGIN = 'https://xfas.in';

// Routes that prerender writes as their own directory (see scripts/prerender.js).
// The server serves build/<route>/index.html at "/<route>/" and 301s the
// slash-less form to it, so the canonical has to carry the slash. Without it the
// page declares a canonical that redirects, and Google files the URL under
// "Page with redirect" and declines to index it.
const DIRECTORY_ROUTES = new Set([
  '/support',
  '/about',
  '/quote',
  '/track',
  '/privacy',
  '/privacy-policy',
  '/terms-and-conditions',
  '/send-courier-from-india-to-usa',
  '/send-courier-from-india-to-uk',
  '/send-courier-from-india-to-europe',
]);

// /privacy and /privacy-policy render the same document. Rather than let Google
// choose between two URLs for one policy, /privacy names the other as canonical
// and only /privacy-policy/ is submitted in the sitemap.
const CANONICAL_OVERRIDES = { '/privacy': '/privacy-policy/' };

// Routes the app actually serves. Anything else fell through to the catch-all
// 404 route, and an error page must not invite indexing or declare itself the
// canonical version of whatever URL produced it.
const KNOWN_ROUTES = new Set([
  '/', '/quote', '/login', '/reset-password', '/track', '/support', '/about',
  '/privacy', '/privacy-policy', '/terms-and-conditions', '/dashboard',
  '/profile', '/my-shipments', '/wallet',
  '/send-courier-from-india-to-usa',
  '/send-courier-from-india-to-uk',
  '/send-courier-from-india-to-europe',
]);
const KNOWN_PREFIXES = ['/admin', '/profile'];

const isKnownRoute = (pathname) => {
  const p = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  return KNOWN_ROUTES.has(p) || KNOWN_PREFIXES.some((k) => p === k || p.startsWith(k + '/'));
};

const canonicalUrl = (pathname) =>
  ORIGIN + (CANONICAL_OVERRIDES[pathname]
    || (DIRECTORY_ROUTES.has(pathname) ? pathname + '/' : pathname));

const SEO = {
  '/': { title: `${BRAND} - Multi-Channel Shipping Solutions`, desc: 'Send parcels, documents & cargo from India to 190+ countries with XFas Logistics. Compare carriers, get instant quotes, and track in real time.' },
  '/quote': { title: `Get a Shipping Quote | ${BRAND}`, desc: 'Get instant courier quotes from India to 190+ countries. Compare carriers and book in minutes.' },
  '/track': { title: `Track Your Shipment | ${BRAND}`, desc: 'Track your international courier and parcel in real time with XFas Logistics.' },
  '/support': { title: `Customer Support & Help | ${BRAND}`, desc: 'Get help with shipping, tracking, KYC and payments. Contact XFas Logistics support.' },
  '/privacy': { title: `Privacy Policy | ${BRAND}`, desc: 'How XFas Logistics collects, uses and protects your data.' },
  '/privacy-policy': { title: `Privacy Policy | ${BRAND}`, desc: 'How XFas Logistics collects, uses and protects your data.' },
  '/terms-and-conditions': { title: `Terms & Conditions | ${BRAND}`, desc: 'Terms of service for using XFas Logistics.' },
  '/dashboard': { title: `My Dashboard | ${BRAND}`, desc: 'Manage your shipments, wallet and profile.' },
  '/my-shipments': { title: `My Shipments | ${BRAND}`, desc: 'View and manage your shipments.' },
  '/wallet': { title: `My Wallet | ${BRAND}`, desc: 'Manage your wallet balance and transactions.' },
  '/profile': { title: `Profile & Settings | ${BRAND}`, desc: 'Manage your account details and KYC.' },
};

const setMeta = (name, content) => {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const setCanonical = (href) => {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const removeCanonical = () => {
  const el = document.querySelector('link[rel="canonical"]');
  if (el && el.parentNode) el.parentNode.removeChild(el);
};

const setProp = (property, content) => {
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const SeoManager = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (!isKnownRoute(pathname)) {
      // The catch-all 404 route. The shell ships an indexable robots tag, so an
      // unknown URL used to be an invitation to index an error page - and a
      // self-canonical made it the preferred version of itself.
      setMeta('robots', 'noindex, follow');
      removeCanonical();
      document.title = `Page Not Found | ${BRAND}`;
      return;
    }
    setMeta('robots', 'index, follow, max-image-preview:large');
    const url = canonicalUrl(pathname);
    setCanonical(url);
    setProp('og:url', url);
    const entry = SEO[pathname];
    if (entry) {
      document.title = entry.title;
      setMeta('description', entry.desc);
      setProp('og:title', entry.title);
      setProp('og:description', entry.desc);
      setMeta('twitter:title', entry.title);
      setMeta('twitter:description', entry.desc);
    }
  }, [pathname]);
  return null;
};

export default SeoManager;
